import { takeEvery, call, take, race, put, cancel, select } from 'redux-saga/effects';
import {
  addUserBulkTransfer,
  bulkAdminTransfer,
  selectBulkTransferError,
  resetBulkTransfer,
  handleBulkTransferResponse,
  setUsers,
  addUser,
  setLoading,
} from '../slices/bulkTransferSlice';
import { bulkAdminTransferRest } from '@/services/transactions/transactions-service';
import { toast } from 'react-toastify';
import { cancelAction, confirmAction, showConfirmation } from '../slices/confirmation';
import { clearMfaRequired, setMfaRequired, otpCodeSubmit, enableMfaUserLock } from '../slices/auth';
import { ErrorCodes } from '../api/base';
import { PayloadAction } from '@reduxjs/toolkit';
import { isAxiosError, AxiosError } from 'axios';
import { AdminBulkTransferUser, AdminBulkTransferDto, TransactionType } from '@repo/common-types';
import { Environment, environment as currentEnvironment } from '@repo/environment';

const isLocalEnvironment = currentEnvironment === Environment.LOCAL || currentEnvironment === Environment.DEVELOPMENT;

function* submitBulkTransfer(users: AdminBulkTransferUser[], transactionType: TransactionType, mfaCode?: string) {
  yield put(setLoading(true));

  const response: AdminBulkTransferDto = yield call(bulkAdminTransferRest, users, transactionType, mfaCode);

  yield put(setLoading(false));

  if (response.error) {
    yield put(
      handleBulkTransferResponse({
        users: users.map((user) => {
          const hasError = response.users.some((u) => u.alias === user.alias);
          return {
            ...user,
            error: hasError ? 'User not found' : null,
          };
        }),
        error: false,
      }),
    );

    toast.error(
      'Transfers will only be processed if all users are valid. Correct the errors for each user and try again.',
    );
    return;
  }

  yield put(
    handleBulkTransferResponse({
      users: response.users,
      error: false,
    }),
  );
  toast.success('Bulk transfer successful');
}

function* handleBulkTransfer(
  action: PayloadAction<{ users: AdminBulkTransferUser[]; transactionType: TransactionType }>,
) {
  try {
    const { users, transactionType } = action.payload;

    yield put(
      showConfirmation({
        title: 'Confirm Bulk Transfer',
        subtitle: `Warning! You are going to transfer to ${users.length} users for a total of $${users.reduce((sum, user) => sum + user.amount, 0).toFixed(2)}`,
        confirmButton: {
          text: 'Confirm Transfer',
          variant: 'danger',
        },
        cancelButton: {
          text: 'Cancel',
          variant: 'secondary',
        },
      }),
    );

    const { confirmed, cancelled } = yield race({
      confirmed: take(confirmAction),
      cancelled: take(cancelAction),
    });

    if (confirmed) {
      if (isLocalEnvironment) {
        yield* submitBulkTransfer(users, transactionType);
        return;
      }

      yield put(setMfaRequired());

      const { cancelMfa, mfaSubmit } = yield race({
        cancelMfa: take(clearMfaRequired),
        mfaSubmit: take(otpCodeSubmit),
      });

      if (cancelMfa) {
        yield cancel();
        return;
      }

      if (mfaSubmit) {
        yield put(clearMfaRequired());
        yield* submitBulkTransfer(users, transactionType, mfaSubmit.payload);
        return;
      }
    }

    if (cancelled) {
      yield cancel();
      return;
    }
  } catch (error: AxiosError | unknown) {
    yield put(setLoading(false));

    if (isAxiosError(error) && error.response?.status === ErrorCodes.INVALID_MFA_CODE) {
      yield put(clearMfaRequired());
      toast.error('Invalid MFA code');
      return;
    }

    if (isAxiosError(error) && error.response?.status === ErrorCodes.LOCKED) {
      yield put(enableMfaUserLock());
      toast.error('Too many failed attempts.');
      return;
    }

    toast.error('Bulk transfer failed');
  }
}

function* handleAddUserBulkTransfer() {
  // has form been submitted
  const error: string | boolean | null = yield select(selectBulkTransferError);

  if (error !== null) {
    yield put(
      showConfirmation({
        title: 'Reset form',
        subtitle: 'Are you sure you want to reset the form?',
        confirmButton: {
          text: 'Reset',
          variant: 'danger',
        },
        cancelButton: {
          text: 'Cancel',
          variant: 'secondary',
        },
      }),
    );

    const { confirmed, cancelled } = yield race({
      confirmed: take(confirmAction),
      cancelled: take(cancelAction),
    });

    if (confirmed) {
      yield put(resetBulkTransfer());
    }

    if (cancelled) {
      yield cancel();
      return;
    }
  }

  yield put(addUser());
}

export function* bulkTransferSaga() {
  yield takeEvery(bulkAdminTransfer.type, handleBulkTransfer);
  yield takeEvery(addUserBulkTransfer, handleAddUserBulkTransfer);
}
